import { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import db from '@/db';
import { formatCurrency } from '@/lib/formatters';
import {
  BarChart3, ArrowLeft, TrendingUp, TrendingDown, AlertTriangle, Moon,
  Trophy, Flame, ChefHat, EyeOff, ArrowUpDown, ArrowUp, ArrowDown, Info,
} from 'lucide-react';

/**
 * v7.41 — Dashboard de Conversão do Cardápio (P2).
 *
 * Cruza `deliveryOrders` × `menuItems` × `crossSellEvents` pra responder:
 *   - Quais pratos vendem MAIS? (Estrelas)
 *   - Quais vendem POUCO? (Fracos)
 *   - Quais NUNCA venderam no período? (Dormentes)
 *   - Quais cross-sells convertem melhor?
 *
 * Janelas: 7d, 30d (default), 90d, all-time.
 *
 * Classificação de força:
 *   - Estrela: top 20% por quantidade vendida (entre os que venderam algo)
 *   - Médio:   meio (40%-80% percentil)
 *   - Fraco:   bottom 40% (mas ≥ 1 venda)
 *   - Dormente: 0 vendas no período
 *
 * NÃO consome contexto/budget extra de backend — toda análise é client-side
 * sobre Dexie (offline-first preservado).
 */

const WINDOWS = [
  { value: 7, label: '7 dias' },
  { value: 30, label: '30 dias' },
  { value: 90, label: '90 dias' },
  { value: 0, label: 'Tudo' },
];

const STRENGTH_META = {
  star:    { label: 'Estrela',  icon: Trophy,         className: 'bg-amber-100 text-amber-800 border-amber-300' },
  medium:  { label: 'Médio',    icon: Flame,          className: 'bg-sky-100 text-sky-800 border-sky-300' },
  weak:    { label: 'Fraco',    icon: TrendingDown,   className: 'bg-orange-100 text-orange-800 border-orange-300' },
  dormant: { label: 'Dormente', icon: Moon,           className: 'bg-zinc-100 text-zinc-600 border-zinc-300' },
};

function StrengthBadge({ strength }) {
  const meta = STRENGTH_META[strength];
  if (!meta) return null;
  const Icon = meta.icon;
  return (
    <span
      data-testid={`strength-badge-${strength}`}
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider border ${meta.className}`}
    >
      <Icon size={10} strokeWidth={2.5} /> {meta.label}
    </span>
  );
}

function SummaryCard({ testId, label, value, sub, icon: Icon, color }) {
  return (
    <div data-testid={testId} className={`bg-white border-2 border-zinc-200 rounded-xl p-4 ${color}`}>
      <div className="flex items-center gap-2 mb-2">
        <Icon size={14} strokeWidth={2.25} />
        <span className="text-[10px] uppercase tracking-[0.18em] font-bold opacity-80">{label}</span>
      </div>
      <p className="font-mono text-2xl font-black tracking-tighter">{value}</p>
      {sub && <p className="text-[11px] mt-0.5 opacity-70">{sub}</p>}
    </div>
  );
}

function daysAgo(iso) {
  if (!iso) return null;
  const ms = Date.now() - new Date(iso).getTime();
  return Math.floor(ms / (1000 * 60 * 60 * 24));
}

function SortIcon({ k, sort }) {
  if (sort.key !== k) return <ArrowUpDown size={10} className="opacity-30" />;
  return sort.dir === 'asc' ? <ArrowUp size={10} /> : <ArrowDown size={10} />;
}

export default function MenuAnalyticsPage() {
  const [items, setItems] = useState([]);
  const [orders, setOrders] = useState([]);
  const [crossEvents, setCrossEvents] = useState([]);
  const [windowDays, setWindowDays] = useState(30);
  const [sort, setSort] = useState({ key: 'quantity', dir: 'desc' });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    (async () => {
      const [menu, ords, evs] = await Promise.all([
        db.menuItems.toArray(),
        db.deliveryOrders.toArray(),
        db.crossSellEvents.toArray(),
      ]);
      if (!mounted) return;
      setItems(menu);
      // Ignora pedidos de treinamento e cancelados na análise.
      setOrders(ords.filter((o) => !o.isTraining && o.status !== 'cancelled'));
      setCrossEvents(evs);
      setLoading(false);
    })();
    return () => { mounted = false; };
  }, []);

  // Janela temporal: cutoff ISO. 0 = sem filtro (all-time).
  const cutoffISO = useMemo(() => {
    if (windowDays === 0) return null;
    const d = new Date();
    d.setDate(d.getDate() - windowDays);
    return d.toISOString();
  }, [windowDays]);

  // Filtra pedidos e eventos pela janela.
  const filteredOrders = useMemo(() => (
    cutoffISO ? orders.filter((o) => (o.createdAt || '') >= cutoffISO) : orders
  ), [orders, cutoffISO]);

  const filteredEvents = useMemo(() => (
    cutoffISO ? crossEvents.filter((e) => (e.timestamp || '') >= cutoffISO) : crossEvents
  ), [crossEvents, cutoffISO]);

  // Agregação por nome de prato (deliveryOrders.items[] guarda apenas `name`).
  // Map<itemName, { quantity, revenue, orderCount, lastOrderAt }>
  const aggregates = useMemo(() => {
    const map = new Map();
    for (const ord of filteredOrders) {
      const seenInOrder = new Set();
      for (const it of (ord.items || [])) {
        const name = it.name;
        if (!name) continue;
        const qty = Number(it.quantity) || 0;
        const price = Number(it.price) || 0;
        const cur = map.get(name) || { quantity: 0, revenue: 0, orderCount: 0, lastOrderAt: null };
        cur.quantity += qty;
        cur.revenue += qty * price;
        if (!seenInOrder.has(name)) {
          cur.orderCount += 1;
          seenInOrder.add(name);
        }
        const at = ord.paidAt || ord.createdAt;
        if (at && (!cur.lastOrderAt || at > cur.lastOrderAt)) cur.lastOrderAt = at;
        map.set(name, cur);
      }
    }
    return map;
  }, [filteredOrders]);

  // Cross-sell por suggestionItemId. Map<itemId, { shown, accepted, declined, rate }>
  const crossByItem = useMemo(() => {
    const map = new Map();
    for (const e of filteredEvents) {
      const id = e.suggestionItemId;
      if (id == null) continue;
      const cur = map.get(id) || { shown: 0, accepted: 0, declined: 0 };
      if (e.event === 'shown') cur.shown += 1;
      else if (e.event === 'accepted') cur.accepted += 1;
      else if (e.event === 'declined') cur.declined += 1;
      map.set(id, cur);
    }
    for (const v of map.values()) v.rate = v.shown > 0 ? (v.accepted / v.shown) * 100 : 0;
    return map;
  }, [filteredEvents]);

  // Combina menuItems + aggregates + crossByItem em uma única linha por prato.
  const rows = useMemo(() => items.map((m) => {
    const agg = aggregates.get(m.name) || { quantity: 0, revenue: 0, orderCount: 0, lastOrderAt: null };
    const cs = crossByItem.get(m.id) || { shown: 0, accepted: 0, declined: 0, rate: 0 };
    return {
      id: m.id,
      name: m.name,
      category: m.category || 'Outros',
      price: Number(m.price) || 0,
      available: m.available !== false,
      quantity: agg.quantity,
      revenue: agg.revenue,
      orderCount: agg.orderCount,
      lastOrderAt: agg.lastOrderAt,
      daysSince: daysAgo(agg.lastOrderAt),
      csShown: cs.shown,
      csAccepted: cs.accepted,
      csRate: cs.rate,
    };
  }), [items, aggregates, crossByItem]);

  // Classificação Estrela / Médio / Fraco / Dormente.
  // Calcula percentis APENAS entre os que venderam (qty > 0). Dormentes ficam fora.
  const classifiedRows = useMemo(() => {
    const sold = rows.filter((r) => r.quantity > 0).map((r) => r.quantity).sort((a, b) => a - b);
    if (sold.length === 0) {
      return rows.map((r) => ({ ...r, strength: r.quantity > 0 ? 'medium' : 'dormant' }));
    }
    const p40 = sold[Math.floor(sold.length * 0.4)] ?? 0;
    const p80 = sold[Math.floor(sold.length * 0.8)] ?? 0;
    return rows.map((r) => {
      if (r.quantity === 0) return { ...r, strength: 'dormant' };
      if (r.quantity >= p80 && sold.length >= 2) return { ...r, strength: 'star' };
      if (r.quantity <= p40) return { ...r, strength: 'weak' };
      return { ...r, strength: 'medium' };
    });
  }, [rows]);

  // Ordenação.
  const sortedRows = useMemo(() => {
    const arr = [...classifiedRows];
    arr.sort((a, b) => {
      const av = a[sort.key];
      const bv = b[sort.key];
      // strings (name, category) → localeCompare; resto → numérico.
      if (typeof av === 'string' && typeof bv === 'string') {
        return sort.dir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
      }
      const an = Number.isFinite(av) ? av : -Infinity;
      const bn = Number.isFinite(bv) ? bv : -Infinity;
      return sort.dir === 'asc' ? an - bn : bn - an;
    });
    return arr;
  }, [classifiedRows, sort]);

  // Métricas de resumo do topo.
  const summary = useMemo(() => {
    const totalItems = classifiedRows.length;
    const counts = { star: 0, medium: 0, weak: 0, dormant: 0 };
    let totalRevenue = 0;
    let totalQuantity = 0;
    for (const r of classifiedRows) {
      counts[r.strength] += 1;
      totalRevenue += r.revenue;
      totalQuantity += r.quantity;
    }
    return { totalItems, ...counts, totalRevenue, totalQuantity, ordersInWindow: filteredOrders.length };
  }, [classifiedRows, filteredOrders]);

  const toggleSort = (key) => {
    setSort((cur) => {
      if (cur.key === key) return { key, dir: cur.dir === 'asc' ? 'desc' : 'asc' };
      // primeiros clicks em colunas numéricas começam DESC (faz mais sentido).
      const numericKeys = ['quantity', 'revenue', 'orderCount', 'price', 'csRate', 'daysSince'];
      return { key, dir: numericKeys.includes(key) ? 'desc' : 'asc' };
    });
  };

  const sortIcon = (k) => <SortIcon k={k} sort={sort} />;

  return (
    <div data-testid="menu-analytics-page" className="p-6 lg:p-8">
      <div className="flex items-start justify-between mb-6 gap-4 flex-wrap">
        <div className="flex items-start gap-4">
          <Link
            to="/cardapio"
            data-testid="back-to-cardapio"
            className="mt-1 w-10 h-10 rounded-lg border-2 border-zinc-200 flex items-center justify-center hover:border-zinc-400 transition-all"
            aria-label="Voltar para o cardápio"
          >
            <ArrowLeft size={16} />
          </Link>
          <div>
            <div className="flex items-center gap-2 mb-1">
              <BarChart3 size={20} className="text-indigo-600" />
              <h2 className="font-heading text-3xl tracking-tight font-bold text-zinc-900">Performance do Cardápio</h2>
            </div>
            <p className="text-sm text-zinc-500">
              Quais pratos vendem, quais não vendem, e o que vale destacar ou aposentar.
            </p>
          </div>
        </div>

        {/* Window selector */}
        <div data-testid="window-selector" className="flex items-center gap-1 bg-white border-2 border-zinc-200 rounded-xl p-1">
          {WINDOWS.map((w) => (
            <button
              key={w.value}
              data-testid={`window-${w.value || 'all'}`}
              onClick={() => setWindowDays(w.value)}
              className={`px-3 h-9 rounded-lg text-xs font-bold transition-all ${
                windowDays === w.value
                  ? 'bg-indigo-600 text-white shadow-sm'
                  : 'text-zinc-500 hover:text-zinc-900'
              }`}
            >
              {w.label}
            </button>
          ))}
        </div>
      </div>

      {/* Resumo */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 mb-6">
        <SummaryCard
          testId="summary-total-items"
          label="Pratos no Cardápio"
          value={summary.totalItems}
          sub={`${summary.ordersInWindow} pedidos`}
          icon={ChefHat}
          color="text-zinc-800"
        />
        <SummaryCard
          testId="summary-total-revenue"
          label="Receita do Período"
          value={formatCurrency(summary.totalRevenue)}
          sub={`${summary.totalQuantity} unidades`}
          icon={TrendingUp}
          color="text-emerald-700"
        />
        <SummaryCard
          testId="summary-stars"
          label="Estrelas"
          value={summary.star}
          sub="Top 20% por venda"
          icon={Trophy}
          color="text-amber-700"
        />
        <SummaryCard
          testId="summary-medium"
          label="Médios"
          value={summary.medium}
          sub="40%–80% percentil"
          icon={Flame}
          color="text-sky-700"
        />
        <SummaryCard
          testId="summary-weak"
          label="Fracos"
          value={summary.weak}
          sub="Vendem pouco"
          icon={AlertTriangle}
          color="text-orange-700"
        />
        <SummaryCard
          testId="summary-dormant"
          label="Dormentes"
          value={summary.dormant}
          sub="0 vendas no período"
          icon={Moon}
          color="text-zinc-600"
        />
      </div>

      {/* Tabela */}
      <div className="bg-white border-2 border-zinc-200 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table data-testid="analytics-table" className="w-full text-sm">
            <thead className="bg-zinc-50 border-b-2 border-zinc-200">
              <tr className="text-left">
                <th className="px-4 py-3 text-[10px] uppercase tracking-wider font-bold text-zinc-500">
                  <button data-testid="sort-name" onClick={() => toggleSort('name')} className="inline-flex items-center gap-1 hover:text-zinc-900">
                    Prato {sortIcon('name')}
                  </button>
                </th>
                <th className="px-4 py-3 text-[10px] uppercase tracking-wider font-bold text-zinc-500">
                  <button data-testid="sort-category" onClick={() => toggleSort('category')} className="inline-flex items-center gap-1 hover:text-zinc-900">
                    Categoria {sortIcon('category')}
                  </button>
                </th>
                <th className="px-4 py-3 text-[10px] uppercase tracking-wider font-bold text-zinc-500 text-right">
                  <button data-testid="sort-quantity" onClick={() => toggleSort('quantity')} className="inline-flex items-center gap-1 hover:text-zinc-900">
                    Unidades {sortIcon('quantity')}
                  </button>
                </th>
                <th className="px-4 py-3 text-[10px] uppercase tracking-wider font-bold text-zinc-500 text-right">
                  <button data-testid="sort-revenue" onClick={() => toggleSort('revenue')} className="inline-flex items-center gap-1 hover:text-zinc-900">
                    Receita {sortIcon('revenue')}
                  </button>
                </th>
                <th className="px-4 py-3 text-[10px] uppercase tracking-wider font-bold text-zinc-500 text-right">
                  <button data-testid="sort-orderCount" onClick={() => toggleSort('orderCount')} className="inline-flex items-center gap-1 hover:text-zinc-900">
                    Pedidos {sortIcon('orderCount')}
                  </button>
                </th>
                <th className="px-4 py-3 text-[10px] uppercase tracking-wider font-bold text-zinc-500 text-right" title="Cross-Sell: aceitos / mostrados">
                  Cross-Sell
                </th>
                <th className="px-4 py-3 text-[10px] uppercase tracking-wider font-bold text-zinc-500">
                  Último pedido
                </th>
                <th className="px-4 py-3 text-[10px] uppercase tracking-wider font-bold text-zinc-500">
                  Status
                </th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={8} className="px-4 py-10 text-center text-sm text-zinc-400" data-testid="analytics-loading">
                  Carregando análise…
                </td></tr>
              )}
              {!loading && sortedRows.length === 0 && (
                <tr><td colSpan={8} className="px-4 py-10 text-center text-sm text-zinc-400" data-testid="analytics-empty">
                  Sem dados pra exibir. Cadastre pratos no cardápio primeiro.
                </td></tr>
              )}
              {!loading && sortedRows.map((r) => (
                <tr
                  key={r.id}
                  data-testid={`analytics-row-${r.id}`}
                  className={`border-b border-zinc-100 hover:bg-zinc-50 transition-colors ${
                    r.strength === 'dormant' ? 'opacity-75' : ''
                  }`}
                >
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <p className="font-semibold text-zinc-900">{r.name}</p>
                      {!r.available && (
                        <span title="Item desativado no cardápio" className="text-zinc-400">
                          <EyeOff size={12} />
                        </span>
                      )}
                    </div>
                    <p className="text-[11px] text-zinc-500 font-mono">{formatCurrency(r.price)} un.</p>
                  </td>
                  <td className="px-4 py-3 text-zinc-600 text-xs">{r.category}</td>
                  <td className="px-4 py-3 text-right font-mono font-bold tabular-nums">
                    {r.quantity || <span className="text-zinc-300">0</span>}
                  </td>
                  <td className="px-4 py-3 text-right font-mono tabular-nums text-zinc-700">
                    {r.revenue > 0 ? formatCurrency(r.revenue) : <span className="text-zinc-300">—</span>}
                  </td>
                  <td className="px-4 py-3 text-right font-mono tabular-nums text-zinc-600">
                    {r.orderCount || <span className="text-zinc-300">0</span>}
                  </td>
                  <td className="px-4 py-3 text-right font-mono text-xs">
                    {r.csShown > 0 ? (
                      <span className="text-zinc-600" title={`${r.csAccepted} aceitos de ${r.csShown} sugestões mostradas`}>
                        {r.csAccepted}/{r.csShown}
                        <span className="text-zinc-400 ml-1">({r.csRate.toFixed(0)}%)</span>
                      </span>
                    ) : (
                      <span className="text-zinc-300">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-xs text-zinc-500">
                    {r.daysSince == null ? (
                      <span className="text-zinc-300">nunca</span>
                    ) : r.daysSince === 0 ? (
                      <span className="text-emerald-700 font-bold">hoje</span>
                    ) : r.daysSince === 1 ? (
                      'ontem'
                    ) : (
                      `há ${r.daysSince} dias`
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <StrengthBadge strength={r.strength} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="mt-4 flex items-start gap-2 text-[11px] text-zinc-500 bg-zinc-50 border border-zinc-200 rounded-lg p-3">
        <Info size={12} className="mt-0.5 shrink-0" />
        <p>
          A classificação é calculada por percentil de unidades vendidas no período selecionado. Pedidos em
          modo treinamento e cancelados são excluídos. Cross-Sell mostra aceitos / sugeridos (rate) — só conta
          quando o prato aparece como sugestão do chef. O agrupamento é por nome do prato (atenção ao renomear).
        </p>
      </div>
    </div>
  );
}
